/* eslint-disable max-len */
import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  CustomButton,
  GenericDeleteModal,
  If,
  InfiniteLoadingSpinner,
  NoPermissions,
  Pagination,
  SearchWithDropdown,
  SidePanel,
  Tabs
} from 'components';
import { EstimatesTabIcon, ExportIcon, SearchIcon } from 'assets/icons';
import routesPath from 'routes/RoutesPath';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectMerchantTeams,
  setCurrentTeam,
  showNotifier
} from 'containers/app/appSlice';
import { debounce } from 'utils/commonUtils';
import { InfoMesssages } from 'types/infoMessages';
import { NotifierTypes } from 'constants/notifierConstants';
import { ESTIMATE_TAB_TITLE, EstimateMoreActions, estimateSearchTypes, estimateTabs } from './constants';
import EstimateFilters from './components/EstimateFilters';
import EstimatesTable from './components/EstimatesTable';
import { usePaginatedGetEstimatesQuery } from './hooks/usePaginatedGetEstimatesQuery';
import { EstimateStatus } from './constants';
import { ShareEstimateModal } from './components/ShareEstimateModal';
import { resetEstimateState } from './estimatesSlice';
import { useDeleteEstimateMutation, useUpdateEstimateStatusMutation } from './api';
import { useLazyExportEstimatesQuery } from './api';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { logAnalyticEvent } from 'utils/analytics';
import { EstimatesCleverTapEvents } from './events';
import { EstimateListItem } from './estimates.model';
import EditEstimateStatusDetailPanel from './components/EditEstimateStatusDetailPanel';
import { SubscriptionStatus, TeamRole } from 'containers/app/app.model';
import EstimateSubscriptionEndedModal from './components/EstimateSubscriptionEndedModal';
import { VoidFn } from 'types/baseTypes';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';

const EstimatesPageCTEvents = {
  [ESTIMATE_TAB_TITLE.ALL_ESTIMATES]: EstimatesCleverTapEvents.webAllEstimatesPageView,
  [ESTIMATE_TAB_TITLE.PENDING]: EstimatesCleverTapEvents.webEstimatesPendingPageView,
  [ESTIMATE_TAB_TITLE.ACCEPTED]: EstimatesCleverTapEvents.webEstimatesAcceptedPageView,
  [ESTIMATE_TAB_TITLE.REJECTED]: EstimatesCleverTapEvents.webEstimatesRejectedPageView
};

interface EstimatesProps {
  estimateSubscriptionPrice: number;
  handleOpenSubscribeToEstimateModal: VoidFn;
}

const Estimates: React.FC<EstimatesProps> = ({ estimateSubscriptionPrice, handleOpenSubscribeToEstimateModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const merchantTeams = useAppSelector(selectMerchantTeams);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const notifications = useAppSelector(getNotifications);

  const [searchText, setSearchText] = useState('');
  const [clicked, setClicked] = useState(false);
  const [currentTab, setCurrentTab] = useState(ESTIMATE_TAB_TITLE.PENDING);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState<string | null>(null);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [openEditStatusDetailsPanel, setOpenEditStatusDetailsPanel] = useState({
    isOpen: false,
    estimateId: null,
    status: null
  });
  const [openSubscriptionExpiredModal, setOpenSubscriptionExpiredModal] = useState(false);
  const [amountFilter, setAmountFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });

  const [triggerExportEstimates] = useLazyExportEstimatesQuery();
  const [deleteEstimate] = useDeleteEstimateMutation();
  const [updateEstimateStatus, { isLoading: isUpdateEstimateStatusLoading }] = useUpdateEstimateStatusMutation();

  const { rootEstimateId, teamId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const shareEstimateId = searchParams.get('shareEstimate');
  const teamIdFromUrl = searchParams.get('teamId');

  const {
    isLoading,
    totalListCount,
    list: estimatesList,
    loadMore,
    resetQuery,
    setQueryParams,
    queryParams,
    isLoadingSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType,
    page
  } = usePaginatedGetEstimatesQuery();

  useEffect(() => {
    if (shareEstimateId) {
      setSelectedEstimateId(shareEstimateId);
      setIsShareModalOpen(true);
      navigate(location.pathname, { replace: true });
    }

    const teamIdVal = teamIdFromUrl ? teamIdFromUrl : teamId;
    if (teamIdVal && currentTeam?.id !== +teamIdVal) {
      const merchantTeam = merchantTeams.find(team => team.id === +teamIdVal);
      if (merchantTeam) {
        dispatch(setCurrentTeam(merchantTeam));
        dispatch(resetEstimateState());
      } else {
        navigate(routesPath.ESTIMATES, { replace: true });
      }
    }
  }, [shareEstimateId, navigate, location.pathname]);

  useEffect(() => {
    const routeState = location.state;
    if (!routeState?.customerId) return;
    const updatedParams = {
      ...queryParams,
      customerIds: [routeState.customerId],
      status: []
    };
    setCurrentTab(ESTIMATE_TAB_TITLE.ALL_ESTIMATES);
    setQueryParams(prevParams => ({
      ...prevParams,
      ...updatedParams
    }));
    navigate(location.pathname, { replace: true });
  }, []);

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedEstimateId(null);
  };

  const handleCreateEstimate = (id: string) => {
    if (!invoicePermissions.createInvoice) {
      onInfoClick(id, InfoMesssages.genericNoPermissionMessage);
      return;
    }
    if (currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED) {
      setOpenSubscriptionExpiredModal(true);
      return;
    }
    if (currentTeam?.subscriptionStatus === SubscriptionStatus.NOT_SUBSCRIBED) {
      handleOpenSubscribeToEstimateModal();
      return;
    }

    dispatch(resetEstimateState());
    navigate(routesPath.ESTIMATES_CREATE);
  };

  const onInfoClick = (id: string, infoMessage: string) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const initiateSearch = (searchTerm?: string) => {
    setQueryParams(prevParams => ({ ...prevParams, search: searchTerm }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), []);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch(null);
  };

  const handleSearchIconClick = () => {
    setClicked(true);
  };

  const onTabClick = (tab: ESTIMATE_TAB_TITLE) => {
    setCurrentTab(tab);
    setQueryParams(prevParams => ({
      ...prevParams,
      status: tab === ESTIMATE_TAB_TITLE.ALL_ESTIMATES ? [] : [tab.toUpperCase() as EstimateStatus]
    }));
    logAnalyticEvent(EstimatesPageCTEvents[tab]);
  };

  useEffect(() => {
    if (currentTeam?.id) {
      setQueryParams(prevParams => ({ ...prevParams, teamId: currentTeam.id }));
      onClearSearch();
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    setQueryParams(prevParams => ({ ...prevParams, amount: getAmountParam() }));
  }, [amountFilter]);

  const getAmountParam = () => {
    const { filterOperation, minAmount, maxAmount } = amountFilter;
    if (filterOperation === AmountFilterOperation.ALL) return null;
    if (filterOperation === AmountFilterOperation.BW) return `${filterOperation}:${minAmount}:${maxAmount}`;
    return `${filterOperation}:${minAmount}`;
  };

  const onEstimateClick = (estimateId: string) => {
    setSelectedEstimateId(estimateId);
    navigate(`${routesPath.ESTIMATES}/${currentTeam.id}/${estimateId}/estimate`);
  };

  const handleUpdateStatus = async (note: string) => {
    await updateEstimateStatus({
      teamId: currentTeam.id,
      estimateId: openEditStatusDetailsPanel.estimateId,
      status: openEditStatusDetailsPanel.status,
      comment: note?.trim() || ''
    }).unwrap();
    refetchEstimatesList();
    setOpenEditStatusDetailsPanel({ isOpen: false, estimateId: null, status: null });
  };

  const handleMoreActionsClick = async (type: EstimateMoreActions, estimate: EstimateListItem) => {
    switch (type) {
      case EstimateMoreActions.SHARE:
        navigateToShareRoute(estimate.id);
        break;
      case EstimateMoreActions.DELETE:
        setEstimateToDelete(estimate.id);
        setShowDeleteModal(true);
        break;
      case EstimateMoreActions.ADD_INVOICE:
        navigate(`${routesPath.INVOICES}/create?estimateId=${estimate.id}`);
        break;
      case EstimateMoreActions.ADD_JOB:
        navigate(`${routesPath.JOBS}/create?estimateId=${estimate.id}`);
        break;
      case EstimateMoreActions.ARCHIVE:
        setSelectedEstimateId(estimate.id);
        setOpenEditStatusDetailsPanel({ isOpen: true, estimateId: estimate.id, status: EstimateStatus.ARCHIVED });
        break;
      case EstimateMoreActions.DUPLICATE:
        navigate(`${routesPath.ESTIMATES}/duplicate/${estimate.id}`);
        break;
      case EstimateMoreActions.VIEW_INVOICE:
        navigate(
          `${routesPath.ESTIMATES}/${currentTeam.id}/${estimate.id}/invoice/${estimate.relatedEntities.invoice.entityId}`
        );
        break;
      case EstimateMoreActions.VIEW_JOB:
        navigate(`${routesPath.ESTIMATES}/${currentTeam.id}/${estimate.id}/jobs/${estimate.id}`);
        break;
      case EstimateMoreActions.MARK_AS_ACCEPTED:
        setSelectedEstimateId(estimate.id);
        setOpenEditStatusDetailsPanel({ isOpen: true, estimateId: estimate.id, status: EstimateStatus.ACCEPTED });
        break;
      default:
        break;
    }
  };

  const handleCloseStatusDetailsPanel = () => {
    setOpenEditStatusDetailsPanel({ isOpen: false, estimateId: null, status: null });
    setSelectedEstimateId(null);
  };

  const navigateToShareRoute = (estimateId: string) => {
    navigate(
      `${routesPath.ESTIMATE_SHARE.replace(':teamId', currentTeam.id.toString())
        .replace(':rootEstimateId', estimateId)
        .replace(':estimateId', estimateId)}`,
      {
        state: DEFAULT_SIDE_PANEL_ROUTE_STATE
      }
    );
  };

  const confirmDeleteEstimate = async () => {
    if (estimateToDelete) {
      setIsActionInProgress(true);
      await handleDeleteEstimate(estimateToDelete);
      setShowDeleteModal(false);
      setEstimateToDelete(null);
      setIsActionInProgress(false);
    }
  };

  const handleDeleteEstimate = async (estimateId: string) => {
    try {
      await deleteEstimate({ teamId: currentTeam.id, estimateId });
      dispatch(
        showNotifier({
          message: { primaryMessage: 'Estimate deleted successfully' },
          type: NotifierTypes.SUCCESS
        })
      );
      refetchEstimatesList();
    } catch (error) {
      dispatch(
        showNotifier({
          message: { primaryMessage: 'Failed to delete estimate' },
          type: NotifierTypes.ERROR
        })
      );
    }
  };

  const handleSidePanelClose = () => {
    navigate(routesPath.ESTIMATES, { replace: true });
    setSelectedEstimateId(null);
  };

  const refetchEstimatesList = () => {
    resetQuery();
  };

  const handleExportEstimates = async () => {
    if (!currentTeam?.id || estimatesList?.length === 0) {
      onInfoClick('estimates-export', InfoMesssages.noEstimatesToExport);
      return;
    }

    try {
      const { fromCreatedAt, toCreatedAt, ...rest } = queryParams;
      triggerExportEstimates({
        teamId: currentTeam.id,
        fromCreatedAt: fromCreatedAt ? new Date(new Date(fromCreatedAt).toUTCString()).toISOString() : null,
        toCreatedAt: toCreatedAt ? new Date(new Date(toCreatedAt).setHours(23, 59, 59, 999)).toISOString() : null,
        ...rest
      });
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Downloading started in the background',
            secondaryMessage: '',
            isMessageHtml: false
          },
          type: NotifierTypes.INFO,
          showClose: false,
          fontStyle: 'text-primary font-normal'
        })
      );
    } catch (error) {
      dispatch(
        showNotifier({
          message: { primaryMessage: 'Failed to export estimates' },
          type: NotifierTypes.ERROR
        })
      );
    }
  };

  const handleCloseSubscriptionExpiredModal = () => {
    setOpenSubscriptionExpiredModal(false);
  };

  const handleSubscriptionExpiredPrimaryAction = () => {
    const isOwner = currentTeam?.role === TeamRole.OWNER;
    if (isOwner) {
      navigate(routesPath.ESTIMATE_INTEGRATION);
    } else {
      handleCloseSubscriptionExpiredModal();
    }
  };

  return (
    <div id="estimate-page-container" className="overflow-y flex h-[calc(100%_-_80px)] flex-col">
      <div className="mb-5 flex flex-row items-center gap-3 px-[30px] pt-6">
        <div className="text-2xl font-bold text-primaryText">Estimates</div>
        <If condition={!clicked}>
          <div onClick={handleSearchIconClick}>
            <SearchIcon className="mx-2 flex lg:hidden" />
          </div>
        </If>
        <SearchWithDropdown
          showSuggestionList={true}
          selectedOption={searchType}
          setSelectedOption={option => {
            setSearchType(option);
            if (searchText?.trim()) {
              setQueryParams(prevParams => ({ ...prevParams }));
            }
          }}
          menuItems={estimateSearchTypes}
          searchId="estimate-search"
          suggestionsList={[]} // Add estimate search suggestions here
          onClearSearchInput={onClearSearch}
          onSearchInputChange={onSearchTermChange}
          searchInputvalue={searchText}
          wrapperStyle={`w-[450px] h-10 cursor-pointer ${
            !invoicePermissions.viewInvoicesByTeam ? 'pointer-events-none' : ''
          } 
          ${!clicked ? 'lg:flex sm:hidden' : ''} `}
          dropDownStyle="w-[150px]"
          autoFocus={false}
        />
        <div className="ml-auto flex gap-2 self-end">
          <CustomButton
            id="create-estimate-button"
            onClick={() => handleCreateEstimate('create-estimate-button')}
            className={'h-[38px] w-[200px] text-primaryBtnText'}
            showDisabledReason={!invoicePermissions.createInvoice}
            StartIcon={EstimatesTabIcon}
            startIconStyle="path-fill-current"
            childrenContainerStyles="mr-1">
            <div className="text-sbase font-semibold">Create estimate</div>
          </CustomButton>
        </div>
      </div>
      {(invoicePermissions.viewInvoicesByTeam || invoicePermissions.viewInvoicesBySelf) && hasPermissionsToView ? (
        <>
          <div className="mb-4 border-b px-[30px]">
            <Tabs tabs={estimateTabs} selectedTab={currentTab} onTabSelect={onTabClick} tabStyle="min-w-[150px] px-4" />
          </div>
          <div className="flex justify-between gap-2 border-b border-secondaryBtn px-[30px] pb-4">
            <EstimateFilters
              statuses={queryParams.status}
              setStatuses={statuses =>
                setQueryParams(prevParams => ({ ...prevParams, status: statuses as EstimateStatus[] }))
              }
              currentTeamId={currentTeam?.id || 0}
              selectedCustomers={queryParams.customerIds}
              setSelectedCustomers={customers =>
                setQueryParams(prevParams => ({ ...prevParams, customerIds: customers }))
              }
              selectedMerchants={queryParams.memberIds}
              setSelectedMerchants={merchants =>
                setQueryParams(prevParams => ({ ...prevParams, memberIds: merchants }))
              }
              currentTab={currentTab}
              startDate={queryParams.fromCreatedAt}
              setStartDate={date => setQueryParams(prevParams => ({ ...prevParams, fromCreatedAt: date }))}
              endDate={queryParams.toCreatedAt}
              setEndDate={date => setQueryParams(prevParams => ({ ...prevParams, toCreatedAt: date }))}
              selectedTeamMembers={queryParams.assignees}
              setSelectedTeamMembers={selectedAssignees =>
                setQueryParams(prevParams => ({ ...prevParams, assignees: selectedAssignees }))
              }
              amountFilter={amountFilter}
              setAmountFilter={setAmountFilter}
              selectedLeadSource={queryParams.leadSource}
              setSelectedLeadSource={leadSources =>
                setQueryParams(prevParams => ({ ...prevParams, leadSource: leadSources }))
              }
              expiresInStartDate={queryParams.fromExpiresAt}
              setExpiresInStartDate={expiresDate =>
                setQueryParams(prevParams => ({ ...prevParams, fromExpiresAt: expiresDate }))
              }
              expiresInEndDate={queryParams.toExpiresAt}
              setExpiresInEndDate={expiresDate =>
                setQueryParams(prevParams => ({ ...prevParams, toExpiresAt: expiresDate }))
              }
            />

            <CustomButton
              id="export-estimates-button"
              onClick={handleExportEstimates}
              className="cursor:pointer min-h-[38px]
              bg-[#A4D7FA] font-lato font-normal normal-case text-primaryText shadow-none hover:bg-[#80C3F1]"
              showDisabledReason={!invoicePermissions.viewInvoicesBySelf && !invoicePermissions.viewInvoicesByTeam}
              StartIcon={ExportIcon}
              startIconStyle="path-fill-current"
              childrenContainerStyles="mr-1">
              <div className="text-sbase font-semibold">Export</div>
            </CustomButton>
          </div>
          {isLoadingSuccess && totalListCount > 0 && (
            <>
              <EstimatesTable
                estimatesList={estimatesList}
                onEstimateClick={onEstimateClick}
                handleMoreActionsClick={handleMoreActionsClick}
                currentSelectedId={selectedEstimateId || rootEstimateId}
              />
            </>
          )}
          {isLoadingSuccess && totalListCount === 0 && !isLoading && (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <EstimatesTabIcon width={40} height={40} />
              <div className="text-sbase font-semibold text-primaryText">No estimates available.</div>
            </div>
          )}
        </>
      ) : (
        <div className="flex h-full items-center justify-center">
          <NoPermissions />
        </div>
      )}
      <If condition={isLoading}>
        <div className={`${totalListCount === 0 ? 'pt-10' : 'pt-0'}`}>
          <InfiniteLoadingSpinner />
        </div>
      </If>
      <If condition={estimatesList?.length !== 0 && hasPermissionsToView}>
        <Pagination currentPage={page} onPageChange={loadMore} totalCount={totalListCount} limit={50} />
      </If>
      <Outlet context={{ handleSidePanelClose, refetchEstimatesList }} />
      {isShareModalOpen && selectedEstimateId && (
        <ShareEstimateModal
          isOpen={isShareModalOpen}
          onClose={handleCloseShareModal}
          estimateId={selectedEstimateId}
          resetEstimatesQuery={refetchEstimatesList}
        />
      )}
      <GenericDeleteModal
        showModal={showDeleteModal}
        isActionInProgress={isActionInProgress}
        title="Confirm Deletion"
        subTitle="Are you sure you want to delete this estimate?"
        primaryBtnLabel="Delete"
        primaryBtnId="delete-estimate-confirm"
        onPrimaryBtnClick={confirmDeleteEstimate}
        secondaryBtnLabel="Cancel"
        secondaryBtnId="cancel-delete-estimate"
        onSecondaryBtnClick={() => setShowDeleteModal(false)}
      />
      <If condition={openEditStatusDetailsPanel.isOpen}>
        <SidePanel isOpen={openEditStatusDetailsPanel.isOpen} onClose={handleCloseStatusDetailsPanel}>
          <EditEstimateStatusDetailPanel
            onBackClick={handleCloseStatusDetailsPanel}
            onSave={handleUpdateStatus}
            isRootState={true}
            status={openEditStatusDetailsPanel.status}
            isActionInProgress={isUpdateEstimateStatusLoading}
          />
        </SidePanel>
      </If>
      <If condition={openSubscriptionExpiredModal}>
        <EstimateSubscriptionEndedModal
          onClose={handleCloseSubscriptionExpiredModal}
          handlePrimaryAction={handleSubscriptionExpiredPrimaryAction}
          price={estimateSubscriptionPrice}
        />
      </If>
    </div>
  );
};

export default Estimates;
